import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import { useContext, useEffect } from 'react';
import { Alert, Button, Card, Container, Form } from 'react-bootstrap';
import { FieldErrors, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ProviderCredentialingDocuments, ProviderService } from '../api';
import { DocumentArrayField } from '../app/components/DocumentArrayField';
import { FileUploader } from '../app/components/FileUploader';
import { FormFeedback } from '../app/components/FormFeedback';
import { FormSubmitContext } from '../app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { providerQueryKeys } from '../app/query-keys-factories/providers';
import { handleApiError } from '../app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../utils/forms';

const DocumentField = ({
  id,
  label = startCase(toLower(id)),
  control,
  readonly = false,
  clearable = true,
  errors,
}: {
  id: string;
  label?: string;
  control: React.ComponentProps<typeof FileUploader>['control'];
  readonly?: boolean;
  clearable?: boolean;
  errors?: FieldErrors;
}) => {
  return (
    <Form.Group controlId={id}>
      <Form.Label className="text-primary fw-bold">{label}</Form.Label>
      <FileUploader
        name={id}
        className="mb-3"
        control={control}
        dest="providers_documents"
        required={false}
        readOnly={readonly}
        isClearable={clearable}
      />
      <FormFeedback clientError={errors?.[id]} />
    </Form.Group>
  );
};

export const DocumentsPage = () => {
  useDocumentTitle('Documents');
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const {
    clearErrors,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm();

  const { data: documents } = useQuery({
    queryKey: providerQueryKeys.detailCredentialingDocuments(),
    queryFn: () => ProviderService.providerCurrentCredentialingDocumentsRetrieve(),
  });

  const queryClient = useQueryClient();
  const mutationProviderCurrentCredentialingDocumentsUpdate = useMutation({
    mutationFn: async (data: ProviderCredentialingDocuments) => {
      await ProviderService.providerCurrentCredentialingDocumentsUpdate({ requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(providerQueryKeys.all());
    },
  });

  const onSubmit = async (data) => {
    try {
      await mutationProviderCurrentCredentialingDocumentsUpdate.mutateAsync(data);
      toast.success('Documents updated successfully.');
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, setError);
      handleApiError(errors);
    }
  };

  const handleSubmitClick = () => {
    clearErrors();
  };

  useEffect(() => {
    reset({
      ...documents,
    });
  }, [documents, reset]);

  return (
    <>
      <div className="bg-primary-dark">
        <Container className="py-2">
          <h1>Documents</h1>
          <p>
            On this page, you can find your most recent contract with Sendit and NPDB report if we
            have it.
          </p>
          <p>
            Any documents added under Private Documents are only available to you and Sendit and are
            <strong>&nbsp;NOT</strong> accessible by Healthcare organizations
          </p>
        </Container>
      </div>
      <Container className="my-4">
        <Card>
          <Card.Body>
            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
              {errors?.root?.non_field_errors && (
                <Alert variant="danger">{errors.root.non_field_errors.message}</Alert>
              )}
              <DocumentField
                id="contract"
                control={control}
                label="Independent Contractor Professional Services Agreement"
                readonly={true}
                clearable={false}
                errors={errors}
              />
              <DocumentField
                id="npdb_report"
                label="NPDB"
                control={control}
                clearable={false}
                errors={errors}
              />
              <DocumentArrayField
                className="mt-3"
                labelClassName="text-primary fw-bold"
                id="misc_files"
                label="Private Documents"
                isRequired={false}
                control={control}
                errors={errors}
              />
              <Button
                className="float-end"
                variant="primary"
                size="lg"
                type="submit"
                onClick={() => handleSubmitClick()}
                disabled={
                  mutationProviderCurrentCredentialingDocumentsUpdate.isLoading || !isSubmitEnabled
                }
              >
                Save Changes
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};
